import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";
import type { GamePassUserBetsResponse } from "./taskBetTypes";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;

const buildGamePassEligibleBetsUrl = (userId: string): string =>
  API_ROUTES.GET_GAME_PASS_USER_ELIGIBLE_BETS.replace("{user_id}", userId);

const fetchGamePassEligibleBets = async (
  userId: string,
  taskId: string,
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
): Promise<GamePassUserBetsResponse> => {
  const response = await getPrivateInstance().get<GamePassUserBetsResponse>(
    buildGamePassEligibleBetsUrl(userId),
    {
      params: {
        page,
        limit,
        task_id: taskId,
      },
    },
  );
  return response.data;
};

type UseGetGamePassEligibleBetsOptions = {
  enabled?: boolean;
  page?: number;
  limit?: number;
  taskId?: string;
};

export const useGetGamePassEligibleBets = (
  userId?: string,
  options?: UseGetGamePassEligibleBetsOptions,
): UseQueryResult<GamePassUserBetsResponse, Error> => {
  const page = options?.page ?? DEFAULT_PAGE;
  const limit = options?.limit ?? DEFAULT_LIMIT;
  const taskId = options?.taskId ?? "";

  return useQuery<GamePassUserBetsResponse, Error>({
    queryKey: [
      "game-pass",
      "users",
      "eligible-bets",
      userId ?? "",
      taskId,
      page,
      limit,
    ],
    queryFn: () => fetchGamePassEligibleBets(userId!, taskId, page, limit),
    enabled: Boolean(userId) && Boolean(taskId) && (options?.enabled ?? true),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });
};

export { buildGamePassEligibleBetsUrl, fetchGamePassEligibleBets };
